import React, { useEffect, useMemo, useState } from 'react';
import { Archive, Search, LayoutGrid, List } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, EmptyState } from './mccUi';
import { PLATFORMS, CONTENT_TYPES } from './mccConstants';
import MccContentCard from './MccContentCard';
import MccProjectList from './MccProjectList';

export default function MccCompleted({ team, onOpenProject }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cards');
  const [filters, setFilters] = useState({ platform: '', content_type: '', person: '', q: '' });
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const res = await mccGet('/marketing/completed', filters);
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load library'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.platform, filters.content_type, filters.person]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const ql = filters.q.toLowerCase();
    return items.filter((i) => (i.title || '').toLowerCase().includes(ql) ||
      (i.description || '').toLowerCase().includes(ql) ||
      (i.hook || '').toLowerCase().includes(ql) ||
      (i.caption || '').toLowerCase().includes(ql));
  }, [items, filters.q]);

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Completed Content Library"
        subtitle="Every published and completed project — fully searchable so the team can reuse, update or repurpose."
        icon={Archive}
        right={
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              onClick={() => setView('cards')}
              data-testid="mcc-workqueue-view-cards"
              className={`px-2.5 py-1.5 ${view === 'cards' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
            ><LayoutGrid className="w-3.5 h-3.5" /></button>
            <button
              onClick={() => setView('list')}
              data-testid="mcc-workqueue-view-list"
              className={`px-2.5 py-1.5 ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
            ><List className="w-3.5 h-3.5" /></button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8 w-64" placeholder="Search library…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <Select value={filters.platform || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All platforms</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.content_type || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, content_type: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {CONTENT_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input className="h-9 w-40" placeholder="Person" value={filters.person}
          onChange={(e) => setFilters((f) => ({ ...f, person: e.target.value }))}
          onBlur={load} />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Archive} title="No completed content yet" body="Once projects are published, they land here forever." />
      ) : view === 'list' ? (
        <MccProjectList items={filtered} nameOf={nameOf} onOpenProject={onOpenProject} groupByStatus />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
